import React, { useEffect, useState, useContext } from 'react'
import { AppSettings } from '../../config/app-settings'
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter
} from '../../components/other/card/card'
import Loader from '../pages/loader'
import GuacLogin from '../../components/RDPServers/GuacLogin'
import { Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import {
  useGetActiveConnectionsQuery,
  useGetConnectionsQuery,
  useTerminateSessionMutation
} from '../../redux/apis/GuacamoleManagment/GuacamoleApiSlice'
import { useNavigate } from 'react-router-dom'

const RDPServers = () => {
  const context = useContext(AppSettings)
  const navigate = useNavigate()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(null)

  const token = localStorage.getItem('guac_token')
  const dataSource = localStorage.getItem('guac_dataSource') || 'postgresql'
  const username = localStorage.getItem('guac_username')

  const {
    data: connections,
    isLoading,
    error,
    refetch
  } = useGetConnectionsQuery({ dataSource }, { skip: !isAuthenticated })

  const {
    data: activeSessions,
    refetch: refetchActive,
    isFetching: isFetchingActive
  } = useGetActiveConnectionsQuery(
    { dataSource },
    {
      skip: !isAuthenticated,
      pollingInterval: 60000
    }
  )

  const [terminateSession] = useTerminateSessionMutation()

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return function cleanUp () {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [])

  useEffect(() => {
    if (token && username) setIsAuthenticated(true)
  }, [token, username])

  useEffect(() => {
    if (!isAuthenticated) return

    const handleFocus = () => {
      console.log('🟢 Window focused: refreshing data...')
      refetch()
      refetchActive()
      setLastRefresh(new Date())
    }

    window.addEventListener('focus', handleFocus)

    const interval = setInterval(() => {
      if (document.hasFocus()) {
        console.log('🔁 Auto-refresh (window focused)')
        refetch()
        refetchActive()
        setLastRefresh(new Date())
      }
    }, 60000)

    return () => {
      window.removeEventListener('focus', handleFocus)
      clearInterval(interval)
    }
  }, [isAuthenticated, refetch, refetchActive])

  const handleLogout = () => {
    Modal.confirm({
      title: 'Logout Confirmation',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to log out?',
      okText: 'Yes, Logout',
      okType: 'danger',
      onOk () {
        localStorage.removeItem('guac_token')
        localStorage.removeItem('guac_username')
        localStorage.removeItem('guac_dataSource')
        setIsAuthenticated(false)
        toast.success('Logged out')
      }
    })
  }

  const handleLaunch = (connectionId, name, activeCount) => {
    if (activeCount > 0) {
      Modal.confirm({
        title: 'Active Session Warning',
        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        content: (
          <>
            <p>
              <strong>{name}</strong> already has {activeCount} active
              connection(s).
            </p>
            <p>Starting a new session may affect existing users. Continue?</p>
          </>
        ),
        okText: 'Yes, Continue',
        onOk: () => launchSession(connectionId, name)
      })
    } else {
      launchSession(connectionId, name)
    }
  }

  const launchSession = id => {
    navigate(`/rdp-servers/${id}`)
  }

  const activeMap = activeSessions ? { ...activeSessions } : {}

  const handleTerminate = sessionId => {
    Modal.confirm({
      title: 'Terminate Session',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: 'Are you sure you want to terminate this active session?',
      okText: 'Terminate',
      okType: 'danger',
      onOk: async () => {
        try {
          await terminateSession({
            dataSource,
            connectionIdentifier: sessionId
          }).unwrap()
          toast.success('Session terminated')
          refetchActive()
          refetch()
        } catch (err) {
          toast.error('Failed to terminate session')
          console.error(err)
        }
      }
    })
  }

  if (!isAuthenticated)
    return <GuacLogin onLoginSuccess={() => setIsAuthenticated(true)} />
  if (isLoading) return <Loader />

  if (error)
    return (
      <Card className='border-danger'>
        <CardBody className='text-center text-danger'>
          <h5>Error loading servers</h5>
          <p>{error?.error || 'Server unreachable or unauthorized'}</p>
          <div className='d-flex justify-content-center gap-2'>
            <button
              className='btn btn-outline-warning'
              onClick={() => {
                refetch()
                refetchActive()
              }}
            >
              <i className='fa fa-sync-alt me-2'></i> Try Again
            </button>
            <button className='btn btn-outline-danger' onClick={handleLogout}>
              <i className='fa fa-sign-out-alt me-2'></i> Re-authenticate
            </button>
          </div>
        </CardBody>
      </Card>
    )

  return (
    <div className='container-fluid'>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h3>Available RDP Servers</h3>
        <div className='d-flex gap-2'>
          <button
            className='btn btn-outline-secondary btn-sm'
            onClick={() => {
              refetch()
              refetchActive()
              setLastRefresh(new Date())
            }}
          >
            <i className='fa fa-sync-alt me-2'></i> Refresh
          </button>
          <button
            className='btn btn-outline-danger btn-sm'
            onClick={handleLogout}
          >
            <i className='fa fa-sign-out-alt me-2'></i> Logout
          </button>
        </div>
      </div>

      <div className='row'>
        {connections && Object.entries(connections).length > 0 ? (
          Object.entries(connections).map(([id, server]) => {
            const active = activeMap[id]
            const activeCount = active ? 1 : server.activeConnections || 0
            return (
              <div className='col-xl-4 col-lg-6 col-md-6 mb-4' key={id}>
                <Card>
                  <CardHeader className='d-flex justify-content-between align-items-center'>
                    <div>
                      <h5 className='mb-0'>{server.name}</h5>
                      <small className='text-muted'>
                        Protocol: {server.protocol.toUpperCase()}
                      </small>
                    </div>
                    <span className='badge bg-primary'>
                      {server.protocol.toUpperCase()}
                    </span>
                  </CardHeader>
                  <CardBody>
                    <p>
                      <strong>Host:</strong>{' '}
                      {server.attributes['hostname'] || 'N/A'}
                    </p>
                    <p>
                      <strong>Active Sessions:</strong> {activeCount}
                    </p>
                    <p>
                      <strong>Max Connections:</strong>{' '}
                      {server.attributes['max-connections'] || 'N/A'}
                    </p>
                  </CardBody>
                  <CardFooter className='text-end'>
                    <button
                      className={`btn ${
                        activeCount > 0 ? 'btn-warning' : 'btn-outline-primary'
                      } btn-sm`}
                      onClick={() => handleLaunch(id, server.name, activeCount)}
                    >
                      <i className='bi bi-play-fill me-1'></i> Start Session
                    </button>
                  </CardFooter>
                </Card>
              </div>
            )
          })
        ) : (
          <Card>
            <CardBody className='text-center text-muted'>
              <h5>No RDP Servers Configured</h5>
            </CardBody>
          </Card>
        )}
      </div>

      <div className='mt-5'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h4>Active Sessions</h4>
          <small className='text-muted'>
            Last refreshed:{' '}
            {lastRefresh ? lastRefresh.toLocaleTimeString() : '—'}
          </small>
        </div>
        {isFetchingActive && <p>Refreshing active sessions...</p>}
        {activeSessions && Object.keys(activeSessions).length > 0 ? (
          <div className='row'>
            {Object.entries(activeSessions).map(([id, session]) => (
              <div className='col-lg-4 col-md-6 mb-4' key={id}>
                <Card className='border-success'>
                  <CardHeader className='d-flex justify-content-between align-items-center'>
                    <strong>
                      {connections?.[id]?.name || `Connection ${id}`}
                    </strong>
                    <span className='badge bg-success'>Active</span>
                  </CardHeader>
                  <CardBody>
                    <p>
                      <strong>User:</strong>{' '}
                      {session.username && session.username.length > 0
                        ? session.username
                        : 'Unknown'}
                    </p>
                    <p>
                      <strong>Start Time:</strong>{' '}
                      {session.startDate
                        ? new Date(session.startDate).toLocaleString()
                        : 'Unknown'}
                    </p>
                  </CardBody>
                  <CardFooter className='text-end'>
                    <button
                      className='btn btn-outline-danger btn-sm'
                      onClick={() => handleTerminate(id)}
                    >
                      <i className='bi bi-x-circle me-1'></i> Terminate
                    </button>
                  </CardFooter>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardBody className='text-center text-muted'>
              <p>No active sessions</p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  )
}

export default RDPServers
